"use client";

import { useEffect } from "react";
import Link from "next/link";
import { ArrowRight, AlertTriangle } from "lucide-react";
import GradientMesh from "@/components/ui/GradientMesh";
import SectionEyebrow from "@/components/ui/SectionEyebrow";
import Footer from "@/components/layout/Footer";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[LaunchLemonade error boundary]", error);
  }, [error]);

  return (
    <main id="main-content">
      <section
        className="relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28 min-h-[75vh] flex items-center"
        aria-labelledby="error-heading"
      >
        <GradientMesh />
        <div className="relative z-10 px-6 md:px-12 max-w-[1200px] w-full mx-auto text-center">
          <SectionEyebrow label="Something broke" />

          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-8 mt-4"
            style={{
              background: "hsl(45 100% 58% / 0.08)",
              border: "1px solid hsl(45 100% 58% / 0.25)",
            }}
            aria-hidden="true"
          >
            <AlertTriangle size={28} style={{ color: "hsl(45 100% 58%)" }} />
          </div>

          <h1
            id="error-heading"
            className="font-heading mx-auto max-w-3xl"
            style={{
              fontSize: "clamp(2.25rem, 1rem + 5vw, 5rem)",
              lineHeight: 0.95,
              letterSpacing: "-0.04em",
              fontWeight: 800,
            }}
          >
            Well, that&apos;s{" "}
            <span className="gold-gradient-text">not supposed to happen.</span>
          </h1>

          <p className="text-[15px] md:text-[17px] text-muted-foreground max-w-xl mx-auto mt-6 leading-snug">
            Something on our end hiccupped. It&apos;s not your browser, it&apos;s
            not your connection, it&apos;s us. Try again, or jump somewhere else
            and we&apos;ll sort it out.
          </p>

          <div className="flex items-center justify-center gap-5 mt-10 flex-wrap">
            <button
              onClick={reset}
              className="btn-gold btn-gold-glow px-7 py-3.5 inline-flex items-center gap-2"
            >
              Try again
              <ArrowRight size={16} aria-hidden="true" />
            </button>
            <Link
              href="/"
              className="text-[13px] font-heading font-semibold text-foreground/80 hover:text-foreground transition-colors inline-flex items-center gap-1.5"
            >
              Back to home
            </Link>
            <Link
              href="/book"
              className="text-[13px] font-heading font-semibold text-foreground/80 hover:text-foreground transition-colors inline-flex items-center gap-1.5"
            >
              Get in touch
            </Link>
          </div>

          {error.digest && (
            <p className="text-[11px] text-muted-foreground/50 font-mono mt-10">
              Error ref: {error.digest}
            </p>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
